import { useState, useEffect, useCallback } from 'react';
import type { PortfolioItem } from '../types/interfaces';

export interface PortfolioSnapshot {
  timestamp: number;
  totalValue: number;
  totalCost: number;
  totalProfit: number;
  totalProfitPercent: number;
  items: Array<{
    id: string;
    symbol: string;
    value: number;
    cost: number;
    profit: number;
    allocation: number; // percentage of total portfolio
  }>;
}

const PORTFOLIO_SNAPSHOTS_KEY = 'cryptoscopePortfolioSnapshots';
const MAX_SNAPSHOTS = 365; // Keep up to 1 year of daily snapshots

export function usePortfolioSnapshots(portfolio: PortfolioItem[]) {
  const [snapshots, setSnapshots] = useState<PortfolioSnapshot[]>(() => {
    const saved = localStorage.getItem(PORTFOLIO_SNAPSHOTS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  // Calculate current portfolio metrics
  const calculateCurrentSnapshot = useCallback((): PortfolioSnapshot | null => {
    if (portfolio.length === 0) return null;

    const totalValue = portfolio.reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.currentPrice || 0),
      0
    );

    const totalCost = portfolio.reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.purchasePrice || item.currentPrice || 0),
      0
    );

    const totalProfit = totalValue - totalCost;
    const totalProfitPercent = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;

    const items = portfolio.map(item => {
      const value = (item.quantity || 0) * (item.currentPrice || 0);
      const cost = (item.quantity || 0) * (item.purchasePrice || item.currentPrice || 0);
      const profit = value - cost;
      const allocation = totalValue > 0 ? (value / totalValue) * 100 : 0;

      return {
        id: item.id,
        symbol: item.symbol,
        value,
        cost,
        profit,
        allocation
      };
    });

    return {
      timestamp: Date.now(),
      totalValue,
      totalCost,
      totalProfit,
      totalProfitPercent,
      items
    };
  }, [portfolio]);

  // Take a snapshot of current portfolio state
  const takeSnapshot = useCallback(() => {
    const snapshot = calculateCurrentSnapshot();
    if (!snapshot) return;

    setSnapshots(prev => {
      const updated = [...prev, snapshot];
      // Keep only the most recent MAX_SNAPSHOTS
      const sorted = updated.sort((a, b) => b.timestamp - a.timestamp);
      return sorted.slice(0, MAX_SNAPSHOTS);
    });
  }, [calculateCurrentSnapshot]);

  // Save snapshots to localStorage
  useEffect(() => {
    localStorage.setItem(PORTFOLIO_SNAPSHOTS_KEY, JSON.stringify(snapshots));
  }, [snapshots]);

  // Auto-snapshot daily (once per day)
  useEffect(() => {
    const lastSnapshot = snapshots[snapshots.length - 1];
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    // If no snapshots or last snapshot is more than 1 day old, take a new one
    if (!lastSnapshot || (now - lastSnapshot.timestamp) >= oneDay) {
      takeSnapshot();
    }

    // Set up interval to check daily
    const interval = setInterval(() => {
      const latest = snapshots[snapshots.length - 1];
      if (!latest || (Date.now() - latest.timestamp) >= oneDay) {
        takeSnapshot();
      }
    }, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(interval);
  }, [snapshots, takeSnapshot]);

  // Get snapshots for a specific time range
  const getSnapshotsInRange = useCallback((days: number): PortfolioSnapshot[] => {
    const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
    return snapshots.filter(s => s.timestamp >= cutoff);
  }, [snapshots]);

  // Get best and worst performers
  const getBestWorstPerformers = useCallback(() => {
    const current = calculateCurrentSnapshot();
    if (!current) return { best: [], worst: [] };

    const sorted = [...current.items].sort((a, b) => {
      const aROI = a.cost > 0 ? (a.profit / a.cost) * 100 : 0;
      const bROI = b.cost > 0 ? (b.profit / b.cost) * 100 : 0;
      return bROI - aROI;
    });

    return {
      best: sorted.slice(0, 3),
      worst: sorted.slice(-3).reverse()
    };
  }, [calculateCurrentSnapshot]);

  return {
    snapshots,
    currentSnapshot: calculateCurrentSnapshot(),
    takeSnapshot,
    getSnapshotsInRange,
    getBestWorstPerformers
  };
}

